import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { GettingStartedChecklist } from "~/components/ui/getting-started-checklist";
import { ProfileQualityCard } from "~/components/ui/profile-quality-card";
import { requireCoachAuth } from "~/lib/coach-middleware";
import type { CoachProgress } from "~/lib/shared/coach-progress-types";

import { EngagementsCard } from "./-components/engagements-card";
import { GreetingHeader } from "./-components/greeting-header";
import { InvoicesCard } from "./-components/invoices-card";
import { MessagesCard } from "./-components/messages-card";
import { NavigationGuide } from "./-components/navigation-guide";
import { SessionsCard } from "./-components/sessions-card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UpcomingSession {
  id: string;
  startAt: string;
  duration: number;
  title: string | null;
  clientName: string;
}

/** A Stripe amount (minor units) in one currency; totals never mix currencies. */
export interface CurrencyTotal {
  currency: string;
  amountCents: number;
}

export interface InvoiceSummary {
  openCount: number;
  openTotals: CurrencyTotal[];
  revenueThisMonth: CurrencyTotal[];
  recentPayments: Array<{
    customerName: string;
    amountCents: number;
    currency: string;
    paidOn: string;
  }>;
}

export interface EngagementSummary {
  activeCount: number;
  upcomingRenewals: number;
}

export interface CoachDashboardData {
  coach: {
    firstName: string | null;
    profilePictureUrl: string | null;
    onboardingCompletedAt: Date | null;
  };
  progress: CoachProgress | null;
  unreadMessageCount: number;
  engagements: EngagementSummary;
  invoiceSummary: InvoiceSummary;
  upcomingSessions: UpcomingSession[];
}

// ---------------------------------------------------------------------------
// Helper queries (plain functions, called within the server function)
// ---------------------------------------------------------------------------

import type { getDBWithInit } from "~/lib/db";
type DB = Awaited<ReturnType<typeof getDBWithInit>>;

async function getUnreadMessageCount(db: DB, coachId: string): Promise<number> {
  const { coaches, conversationParticipants, messages } =
    await import("~/../drizzle/schema");
  const { and, eq, gt, ne, or, isNull, sql } = await import("drizzle-orm");

  const [coach] = await db
    .select({ userId: coaches.userId })
    .from(coaches)
    .where(eq(coaches.id, coachId))
    .limit(1);

  if (!coach?.userId) return 0;

  const [result] = await db
    .select({ count: sql<number>`cast(count(*) as integer)` })
    .from(conversationParticipants)
    .innerJoin(
      messages,
      eq(messages.conversationId, conversationParticipants.conversationId),
    )
    .where(
      and(
        eq(conversationParticipants.userId, coach.userId),
        ne(messages.senderUserId, coach.userId),
        or(
          isNull(conversationParticipants.lastReadAt),
          gt(messages.createdAt, conversationParticipants.lastReadAt),
        ),
      ),
    );

  return result?.count ?? 0;
}

async function getEngagementSummary(
  db: DB,
  coachId: string,
): Promise<EngagementSummary> {
  const { engagements } = await import("~/../drizzle/schema");
  const { EngagementStatus } = await import("~/lib/schemas/db-enums");
  const { and, eq, sql, gte, lte } = await import("drizzle-orm");

  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  const [activeResult] = await db
    .select({ count: sql<number>`cast(count(*) as integer)` })
    .from(engagements)
    .where(
      and(
        eq(engagements.coachId, coachId),
        eq(engagements.status, EngagementStatus.ACTIVE),
      ),
    );

  const [renewalResult] = await db
    .select({ count: sql<number>`cast(count(*) as integer)` })
    .from(engagements)
    .where(
      and(
        eq(engagements.coachId, coachId),
        eq(engagements.status, EngagementStatus.ACTIVE),
        lte(engagements.dateEnd, thirtyDaysFromNow),
        gte(engagements.dateEnd, new Date()),
      ),
    );

  return {
    activeCount: activeResult?.count ?? 0,
    upcomingRenewals: renewalResult?.count ?? 0,
  };
}

async function getInvoiceSummaryData(
  db: DB,
  coachId: string,
): Promise<InvoiceSummary> {
  const { invoices } = await import("~/../drizzle/schema");
  const { and, eq, sql, gte, inArray, desc } = await import("drizzle-orm");

  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);

  const [openInvoices, recentPaid, monthRevenue] = await Promise.all([
    db
      .select({
        currency: invoices.currency,
        count: sql<number>`cast(count(*) as integer)`,
        total: sql<number>`coalesce(sum(amount), 0)::integer`,
      })
      .from(invoices)
      .where(
        and(
          eq(invoices.coachId, coachId),
          inArray(invoices.status, ["open", "draft"]),
        ),
      )
      .groupBy(invoices.currency)
      .orderBy(invoices.currency),
    db
      .select({
        customerName: invoices.customerName,
        amount: invoices.amount,
        currency: invoices.currency,
        paidOn: invoices.paidOn,
      })
      .from(invoices)
      .where(and(eq(invoices.coachId, coachId), eq(invoices.status, "paid")))
      .orderBy(desc(invoices.paidOn))
      .limit(3),
    db
      .select({
        currency: invoices.currency,
        total: sql<number>`coalesce(sum(amount), 0)::integer`,
      })
      .from(invoices)
      .where(
        and(
          eq(invoices.coachId, coachId),
          eq(invoices.status, "paid"),
          gte(invoices.paidOn, startOfMonth),
        ),
      )
      .groupBy(invoices.currency)
      .orderBy(invoices.currency),
  ]);

  return {
    openCount: openInvoices.reduce((sum, row) => sum + row.count, 0),
    openTotals: openInvoices.map((row) => ({
      currency: row.currency,
      amountCents: row.total,
    })),
    revenueThisMonth: monthRevenue.map((row) => ({
      currency: row.currency,
      amountCents: row.total,
    })),
    recentPayments: recentPaid.flatMap((p) =>
      p.paidOn
        ? [
            {
              customerName: p.customerName,
              amountCents: p.amount,
              currency: p.currency,
              paidOn: p.paidOn.toISOString(),
            },
          ]
        : [],
    ),
  };
}

async function getUpcomingSessionsData(
  db: DB,
  coachId: string,
): Promise<UpcomingSession[]> {
  const { sessions, engagements, users } = await import("~/../drizzle/schema");
  const { SessionStatus } = await import("~/lib/schemas/db-enums");
  const { and, eq, gte } = await import("drizzle-orm");

  const rows = await db
    .select({
      id: sessions.id,
      startAt: sessions.startAt,
      duration: sessions.duration,
      title: sessions.title,
      clientFirstName: users.firstName,
      clientLastName: users.lastName,
    })
    .from(sessions)
    .innerJoin(engagements, eq(sessions.engagementId, engagements.id))
    .innerJoin(users, eq(engagements.userId, users.id))
    .where(
      and(
        eq(engagements.coachId, coachId),
        eq(sessions.status, SessionStatus.SCHEDULED),
        gte(sessions.startAt, new Date()),
      ),
    )
    .orderBy(sessions.startAt)
    .limit(3);

  return rows.map((r) => ({
    id: r.id,
    startAt: r.startAt.toISOString(),
    duration: r.duration,
    title: r.title,
    clientName:
      [r.clientFirstName, r.clientLastName].filter(Boolean).join(" ") ||
      "Client",
  }));
}

// ---------------------------------------------------------------------------
// Server function
// ---------------------------------------------------------------------------

const getCoachDashboardData = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }): Promise<CoachDashboardData> => {
    const coachShared = context.coach;
    const { getDBWithInit } = await import("~/lib/db");
    const db = await getDBWithInit();

    const { eq, sql } = await import("drizzle-orm");
    const { certifications, coachesCcos } = await import("~/../drizzle/schema");
    const {
      calculateGettingStarted,
      deriveMarketplaceStatus,
      calculateProfileQuality,
    } = await import("~/lib/coach-progress");

    const [
      coachFull,
      unreadMessageCount,
      engagementData,
      invoiceSummary,
      upcomingSessions,
    ] = await Promise.all([
      db.query.coaches.findFirst({
        where: { id: coachShared.id },
        with: {
          profile: true,
          details: true,
          mainOffer: true,
          expertises: true,
        },
      }),
      getUnreadMessageCount(db, coachShared.id),
      getEngagementSummary(db, coachShared.id),
      getInvoiceSummaryData(db, coachShared.id),
      getUpcomingSessionsData(db, coachShared.id),
    ]);

    let progress: CoachProgress | null = null;

    if (coachFull) {
      const [certCount, ccoCount] = await Promise.all([
        db
          .select({ count: sql<number>`count(*)::int` })
          .from(certifications)
          .where(eq(certifications.coachId, coachFull.id)),
        db
          .select({ count: sql<number>`count(*)::int` })
          .from(coachesCcos)
          .where(eq(coachesCcos.coachId, coachFull.id)),
      ]);

      const gettingStarted = calculateGettingStarted(
        {
          ...coachFull,
          expertises: coachFull.expertises.map((e) => ({
            id: e.subcategoryId,
          })),
        },
        coachFull.profile,
        coachFull.details,
        {
          hasCertifications: (certCount[0]?.count ?? 0) > 0,
          hasCcoMemberships: (ccoCount[0]?.count ?? 0) > 0,
        },
      );

      const marketplaceStatus = deriveMarketplaceStatus(
        {
          marketplaceApprovedAt: coachFull.marketplaceApprovedAt ?? null,
          marketplaceRevokedAt: coachFull.marketplaceApprovalRevokedAt ?? null,
          marketplaceReturnedAt:
            coachFull.marketplaceApprovalReturnedAt ?? null,
          marketplaceRequestedAt:
            coachFull.marketplaceApprovalRequestedAt ?? null,
        },
        gettingStarted.completedCount === gettingStarted.totalCount,
      );

      const profileQuality = calculateProfileQuality(
        coachFull.profile,
        coachFull.details,
        coachFull.mainOffer,
        coachFull.expertises,
      );

      progress = { gettingStarted, marketplaceStatus, profileQuality };
    }

    return {
      coach: {
        firstName: coachShared.firstName,
        profilePictureUrl: coachShared.profilePictureUrl,
        onboardingCompletedAt: coachShared.onboarding_completed_at,
      },
      progress,
      unreadMessageCount,
      engagements: engagementData,
      invoiceSummary,
      upcomingSessions,
    };
  });

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/embed/coach-dashboard/")({
  loader: async () => {
    try {
      const data = await getCoachDashboardData();
      return { data };
    } catch (e) {
      // requireCoachAuth throws a non-serializable Response on auth failure.
      // Re-throw as a plain Error so seroval can serialize it for errorComponent.
      if (e instanceof Response) {
        const body = await e.text().catch(() => "(unreadable body)");
        // 401/403 is the expected "no valid locationId / coach" case for the
        // GHL embed — log at warn so it doesn't page Sentry as an error.
        const log =
          e.status === 401 || e.status === 403 ? console.warn : console.error;
        log(
          `[coach-dashboard] loader failed: ${e.status} ${e.statusText} — ${body}`,
        );
      } else {
        console.error("[coach-dashboard] loader failed:", e);
      }
      throw new Error("Unable to load dashboard");
    }
  },
  component: CoachDashboardEmbed,
  errorComponent: DashboardError,
});

function DashboardError() {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white p-6">
      <div className="text-center">
        <p className="text-text-secondary text-sm">
          Unable to load your dashboard. Please refresh the page or contact
          support.
        </p>
      </div>
    </div>
  );
}

function CoachDashboardEmbed() {
  const { data } = Route.useLoaderData();

  return (
    <div className="min-h-screen w-full bg-white p-4 md:p-6">
      <div className="mx-auto max-w-4xl space-y-4">
        <GreetingHeader firstName={data.coach.firstName} />

        {data.progress && data.progress.marketplaceStatus !== "approved" && (
          <GettingStartedChecklist
            gettingStarted={data.progress.gettingStarted}
            marketplaceStatus={data.progress.marketplaceStatus}
            newTab
          />
        )}
        {data.progress && (
          <ProfileQualityCard
            profileQuality={data.progress.profileQuality}
            newTab
          />
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <MessagesCard unreadCount={data.unreadMessageCount} />
          <SessionsCard sessions={data.upcomingSessions} />
          <InvoicesCard summary={data.invoiceSummary} />
          <EngagementsCard summary={data.engagements} />
        </div>

        <NavigationGuide />
      </div>
    </div>
  );
}
